import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, Plus, X } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import {
  MAX_REMINDERS,
  REMINDER_OPTIONS,
  findReminderLabelKey,
} from "@/lib/agendaReminders";

interface RemindersEditorProps {
  reminders: number[];
  onChange: (next: number[]) => void;
}

// Inline reminder list editor — cap MAX_REMINDERS (2 by spec).
//
// Pattern: each existing reminder renders as a chip with the offset label
// (e.g. "10 min antes") + an inline remove button.
// Tapping "+ adicionar" reveals the preset picker; the picker filters out
// values the event already has so the same offset can't be added twice.
// The "+" button disables once the count hits the cap.
//
// Persistence is the parent's responsibility — we just pump state up via
// onChange. Loading + saving against event_reminders happens in
// CreateEventModal / EditEventModal.
const RemindersEditor: React.FC<RemindersEditorProps> = ({ reminders, onChange }) => {
  const { t } = useLanguage();
  const [picking, setPicking] = useState(false);

  const remove = (minutes: number) => {
    onChange(reminders.filter((m) => m !== minutes));
  };

  const add = (minutes: number) => {
    if (reminders.includes(minutes)) {
      setPicking(false);
      return;
    }
    if (reminders.length >= MAX_REMINDERS) {
      setPicking(false);
      return;
    }
    onChange([...reminders, minutes].sort((a, b) => a - b));
    setPicking(false);
  };

  const atCap = reminders.length >= MAX_REMINDERS;
  const availableOptions = REMINDER_OPTIONS.filter(
    (o) => !reminders.includes(o.value),
  );

  return (
    <div className="mb-4">
      <span
        className="text-sm font-medium text-muted-foreground mb-1.5 block"
      >
        <Bell size={9} className="inline mr-1 -mt-px" />
        {t("reminders.section.title")}
      </span>

      <div className="space-y-1.5">
        {reminders.length === 0 && (
          <p className="text-[11px] font-body" style={{ color: "hsl(218 14% 50%)" }}>
            {t("reminders.section.empty")}
          </p>
        )}

        {reminders.map((minutes) => {
          const labelKey = findReminderLabelKey(minutes);
          return (
          <motion.div
            key={minutes}
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            className="flex items-center gap-2 rounded-xl px-3 py-2"
            style={{
              background: "hsl(187 72% 50% / 0.08)",
              border: "1px solid hsl(187 72% 50% / 0.25)",
            }}
          >
            <Bell size={11} style={{ color: "hsl(187 72% 60%)" }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium" style={{ color: "hsl(187 72% 65%)" }}>
                {labelKey === "reminders.preset.custom" ? minutes + " " + t("agendaAction.minutesBefore") : t(labelKey)}
              </p>
            </div>
            <button
              type="button"
              onClick={() => remove(minutes)}
              aria-label={t("reminders.remove")}
              className="min-w-11 min-h-11 rounded-full flex items-center justify-center transition-colors hover:bg-red-500/[0.12]"
            >
              <X size={11} style={{ color: "hsl(0 60% 60% / 0.7)" }} />
            </button>
          </motion.div>
        ); })}

        <AnimatePresence>
          {picking && availableOptions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap gap-1.5 pt-1.5">
                {availableOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => add(opt.value)}
                    className="px-3 py-1.5 rounded-lg text-[11px] font-display font-medium tracking-wide transition-all"
                    style={{
                      background: "transparent",
                      border: "1px solid hsl(187 72% 50% / 0.30)",
                      color: "hsl(187 72% 60%)",
                    }}
                  >
                    {t(opt.labelKey)}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPicking(false)}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-display tracking-wide"
                  style={{ color: "hsl(218 14% 55%)" }}
                >
                  {t("common.cancel")}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!picking && (
          <button
            type="button"
            onClick={() => setPicking(true)}
            disabled={atCap}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-display font-medium tracking-wide transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: "transparent",
              border: "1px dashed hsl(187 72% 50% / 0.35)",
              color: atCap ? "hsl(218 14% 45%)" : "hsl(187 72% 60%)",
            }}
          >
            <Plus size={11} />
            {atCap ? t("reminders.section.capReached") : t("reminders.section.add")}
          </button>
        )}
      </div>
    </div>
  );
};

export default RemindersEditor;
